/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import CloseIcon from '@mui/icons-material/Close';
import {Modal, Box, IconButton, Typography} from '@mui/material';
import {SparkLineChart, LineChartPro} from '@mui/x-charts-pro';
import {format} from 'date-fns';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {useState} from 'react';
import {useChartMarkClick} from './SuperChartClickContext';
import type {FeatureFlagSet} from '../../flags';
import {getColorFromPercentage} from '../../services/getColorFromPercentage';

type ChartDataPoint = {x: number; y: number; id?: string};

type SuperChartProps = {
  data: {time: number; value: number; id?: string}[];
  type: 'percentage' | 'number';
  title?: string;
};

export default function SuperChart({data, type, title}: SuperChartProps) {
  const {featureSuperChart} = useFlags<FeatureFlagSet>();
  const chartMarkClick = useChartMarkClick();
  const [open, setOpen] = useState(false);

  if (!featureSuperChart) return null;

  const formattedData = formatData(data);

  if (!formattedData.length) return null;
  const handleMarkClick = (_event: any, {dataIndex}: {dataIndex?: number}) => {
    if (typeof dataIndex === 'number') {
      const clickedItem = formattedData[dataIndex];
      if (clickedItem.id) {
        chartMarkClick(clickedItem.id);
      }
    }
  };

  const sparkSeries = formattedData.map((point) => point.y);

  let sparkColor: string | null = null;
  if (type === 'percentage' && sparkSeries.length) {
    const average = sparkSeries.reduce((acc, cur) => acc + cur, 0) / sparkSeries.length;
    sparkColor = getColorFromPercentage(average);
  }

  return (
    <>
      <Box
        sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'}}
        onClick={() => setOpen(true)}
      >
        <SparkLineChart data={sparkSeries} height={50} colors={sparkColor ? [sparkColor] : undefined} />
      </Box>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            p: 4,
            outline: 'none',
          }}
        >
          {title && (
            <Typography variant={'h6'} textAlign={'center'}>
              {title}
            </Typography>
          )}
          <LineChartPro
            dataset={formattedData}
            series={[{dataKey: 'y'}]}
            xAxis={[
              {
                dataKey: 'x',
                scaleType: 'time',
                valueFormatter: (value) => format(new Date(value), 'MMM dd, yyyy'),
                domainLimit: 'nice',
                tickInterval: 'auto',
                zoom: true,
              },
            ]}
            yAxis={[{min: type === 'percentage' ? 0 : undefined}]}
            width={920}
            height={500}
            colors={sparkColor ? [sparkColor] : undefined}
            onMarkClick={handleMarkClick}
          />
          <IconButton sx={{position: 'absolute', top: 8, right: 8}} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Modal>
    </>
  );
}

function formatData(data: {time: number; value: number; id?: string}[]): ChartDataPoint[] {
  return (
    data
      // Map to x and y
      .map((point) => ({x: point.time, y: point.value, id: point.id}))
      // Sort by time
      .sort((a, b) => a.x - b.x)
      // Filter non numbers
      .filter((point) => !isNaN(point.x) && !isNaN(point.y))
  );
}
